import { abweisen, verlangen } from "../gemeinsam/index.js";
import { entfernung, istWildart, type Wildart } from "../drueckjagd/oeffentlich.js";
import { genauigkeitLesen, HIMMELSRICHTUNGEN, positionLesen, type Himmelsrichtung, type Position } from "../schuss/oeffentlich.js";

/** Wertebereiche des Kontexts Lage (Fachsprache, Domänenmodell §6). */

export const ANSTELLSTATUS = ["offen", "angestellt", "am Stand", "abgeholt"] as const;
export type Anstellstatus = (typeof ANSTELLSTATUS)[number];

export const TREIBERGRUPPENSTATUS = ["nicht gestartet", "am Start", "am Sammelpunkt"] as const;
export type Treibergruppenstatus = (typeof TREIBERGRUPPENSTATUS)[number];

export const NOTFALLARTEN = ["Person verletzt", "Hund verletzt", "sonstiger Notfall"] as const;
export type Notfallart = (typeof NOTFALLARTEN)[number];

export const NOTRUFSTATUS = ["abgesetzt", "quittiert", "erledigt"] as const;
export type Notrufstatus = (typeof NOTRUFSTATUS)[number];

export const VERMISSTSTATUS = ["vermisst", "zurück"] as const;
export type Vermisststatus = (typeof VERMISSTSTATUS)[number];

export type { Position, Himmelsrichtung, Wildart };
export { HIMMELSRICHTUNGEN };

/** Eine Meldung im Feld: optional mit der Position des Geräts und ihrer Genauigkeit (LIM-0009). */
export interface Geraeteposition {
  position: Position | null;
  genauigkeit: number | null;
}

/** Position und Genauigkeit aus den Befehlsdaten; beide optional, eine angegebene Position muss gültig sein (`position-ungueltig`). */
export function geraetepositionLesen(position: unknown, genauigkeit: unknown): Geraeteposition {
  return {
    position: position === undefined || position === null ? null : positionLesen(position),
    genauigkeit: genauigkeitLesen(genauigkeit),
  };
}

export function bemerkungLesen(w: unknown): string | null {
  return typeof w === "string" && w.trim() !== "" ? w.trim() : null;
}

/** Bewegungsrichtung einer Wildsichtung: optional, sonst eine der acht Himmelsrichtungen (Wertobjekt `Himmelsrichtung`). */
export function bewegungsrichtungLesen(w: unknown): Himmelsrichtung | null {
  if (w === undefined || w === null) return null;
  verlangen(typeof w === "string" && (HIMMELSRICHTUNGEN as readonly string[]).includes(w), "richtung-ungueltig");
  return w as Himmelsrichtung;
}

export function wildartLesen(w: unknown): Wildart {
  verlangen(typeof w === "string" && w.trim() !== "", "wildart-fehlt");
  verlangen(istWildart(w), "wildart-unbekannt");
  return w;
}

/** Anzahl gesichteter Stücke: eine ganze Zahl ab 1. */
export function anzahlLesen(w: unknown): number {
  if (typeof w !== "number" || !Number.isInteger(w) || w < 1) abweisen("unzulaessige-angaben");
  return w;
}

export function notfallartLesen(w: unknown): Notfallart {
  verlangen(typeof w === "string" && (NOTFALLARTEN as readonly string[]).includes(w), "unzulaessige-angaben");
  return w as Notfallart;
}

/**
 * Hinweis „Position weicht vom Stand ab“ (F-114; bewusst keine Invariante, LIM-0009): Die gemeldete Position liegt auch
 * nach Abzug ihrer Genauigkeit mehr als {@link ABWEICHUNG_HINWEIS_M} vom Stand entfernt; „deutlich“ ist so festgelegt in
 * Anforderungen §7a K79 (b).
 */
export const ABWEICHUNG_HINWEIS_M = 100;

export function positionWeichtAb(stand: Position, gemeldet: Position, genauigkeit: number | null): boolean {
  return entfernung(stand, gemeldet) - (genauigkeit ?? 0) > ABWEICHUNG_HINWEIS_M;
}

/** Nächstgelegener Punkt (z. B. Rettungspunkt zur Position des Geräts, K48); ohne Punkte `null`. */
export function naechstgelegen<T extends Position>(von: Position, punkte: readonly T[]): T | null {
  let bester: T | null = null;
  let abstand = Infinity;
  for (const p of punkte) {
    const d = entfernung(von, p);
    if (d < abstand) {
      bester = p;
      abstand = d;
    }
  }
  return bester;
}

/**
 * Entfernung in Metern und Richtung in acht Himmelsrichtungen von `von` nach `nach` (Anfangskurs auf dem Großkreis):
 * eingehender Notruf „Entfernung vom eigenen Gerät mit Richtung“ (design/README §9.10).
 */
export function abstandUndRichtung(von: Position, nach: Position): { meter: number; richtung: Himmelsrichtung } {
  const rad = (g: number) => (g * Math.PI) / 180;
  const p1 = rad(von.Breite);
  const p2 = rad(nach.Breite);
  const dl = rad(nach.Länge - von.Länge);
  const kurs = (Math.atan2(Math.sin(dl) * Math.cos(p2), Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl)) * 180) / Math.PI;
  return { meter: entfernung(von, nach), richtung: HIMMELSRICHTUNGEN[Math.round(((kurs + 360) % 360) / 45) % 8]! };
}

/** Entfernung für die Anzeige: unter 1 km auf 10 m gerundet („230 m“), sonst in km mit einer Nachkommastelle („1,2 km“). */
export function entfernungText(meter: number): string {
  if (meter < 995) return `${Math.round(meter / 10) * 10} m`;
  return `${(meter / 1000).toFixed(1).replace(".", ",")} km`;
}

function pruefsumme(text: string, saat: number): number {
  let h1 = 0xdeadbeef ^ saat;
  let h2 = 0x41c6ce57 ^ saat;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 2654435761);
    h2 = Math.imul(h2 ^ c, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/**
 * Kennung einer Treibergruppe in der Identität der Treibergruppenmeldung (ereignisspeicher.md §3: DrückjagdId `+`
 * RevierId `+` Kennung der Treibergruppe). Eine Treibergruppe hat nur ihre Bezeichnung; die Kennung ist deren
 * Prüfsumme (ASCII, ohne lesbaren Wert, für dieselbe Bezeichnung immer dieselbe). Gefunden wird der Strom über den
 * Bezug `treibergruppe` (Bezeichnung).
 */
export function treibergruppenKennung(bezeichnung: string): string {
  return `tg_${[1, 2].map((s) => pruefsumme(bezeichnung, s).toString(36).padStart(11, "0")).join("")}`;
}
